import React, { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, FileText, BarChart3, Download } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [types, setTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [type, setType] = useState("full");
  const [programId, setProgramId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    api.get("/reports/types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/programs").then((r) => { setPrograms(r.data); if (r.data[0]) setProgramId(r.data[0].id); }).catch(() => {});
  }, []);

  const scope = types.find((t) => t.key === type)?.scope || "program";

  const runPreview = () => {
    if (scope === "program" && !programId) return;
    setLoading(true); setPreview(null);
    const url = scope === "program" ? `/reports/preview?type=${type}&program_id=${programId}` : `/reports/preview?type=${type}`;
    api.get(url).then((r) => setPreview(r.data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); }).finally(() => setLoading(false));
  };
  useEffect(() => { if (types.length) runPreview(); }, [type, programId, types.length]);

  const download = async (format) => {
    setDownloading(format);
    try {
      const token = localStorage.getItem("token");
      const q = scope === "program" ? `type=${type}&program_id=${programId}&format=${format}` : `type=${type}&format=${format}`;
      const res = await fetch(`${API}/reports/download?${q}`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error("فشل التحميل");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${type}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("تم تنزيل التقرير");
    } catch (e) { toast.error("تعذّر تنزيل التقرير"); }
    finally { setDownloading(""); }
  };

  return (
    <>
      <PageHeader title="التقارير" subtitle="عرض وتصدير تقارير الحضور والدرجات والساعات بصيغة Excel و PDF" />

      <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-navy">نوع التقرير</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="report-type" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {scope === "program" && (
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-navy">البرنامج</label>
              <Select value={programId} onValueChange={setProgramId}>
                <SelectTrigger data-testid="report-program" className="rounded-xl"><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-2">
            <Button data-testid="export-excel" onClick={() => download("excel")} disabled={downloading || !preview} className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700">
              {downloading === "excel" ? "..." : <><FileSpreadsheet className="me-2 h-4 w-4" /> تصدير Excel</>}
            </Button>
            <Button data-testid="export-pdf" onClick={() => download("pdf")} disabled={downloading || !preview} className="flex-1 rounded-xl bg-red-500 hover:bg-red-600">
              {downloading === "pdf" ? "..." : <><FileText className="me-2 h-4 w-4" /> تصدير PDF</>}
            </Button>
          </div>
        </div>
      </div>

      {loading ? <PageLoader /> : !preview ? <EmptyState icon={BarChart3} title="اختر تقريرًا لعرضه" /> : (
        <div className="rounded-2xl border border-slate-100 bg-white soft-shadow overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 p-5">
            <h3 className="font-heading text-lg font-bold text-navy">{preview.title}</h3>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">{preview.count} سجل</span>
          </div>
          {preview.rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">لا توجد بيانات في هذا التقرير</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-start text-sm" data-testid="report-table">
                <thead className="bg-navy text-white">
                  <tr>{preview.headers.map((h, i) => <th key={i} className="p-3 text-start font-semibold whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-slate-100 hover:bg-slate-50/50">
                      {row.map((cell, ci) => <td key={ci} className="p-3 text-slate-600 whitespace-nowrap">{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
